import Vue from "vue";
import { mapGetters, mapState } from "vuex";
import pinIcon from "@/assets/svg/icons/signify-icons_39-pin.svg?inline";
import { isPhaseIn, isPhaseOut, isPreCr } from "@/utils/Lifecycle";
import { REGION_NA } from "@/service/Constants";

function roundN(num, n) {
  if (num % 1 != 0) {
    return parseFloat(
      Math.round(num * Math.pow(10, n)) / Math.pow(10, n)
    ).toFixed(n);
  }
  return num;
}

const InputVoltages = {
  i120Vac: 9,
  i200Vac: 10,
  i230Vac: 11,
  i240Vac: 12,
  i277Vac: 13,
  i347Vac: 14,
  i480Vac: 15,
  i120_277Vac: 16,
  i347_480Vac: 17,
  i120_230Vac: 18,
};

// Todo this should be decided in the backend
function getInputVoltage(inputVoltages) {
  let label = [],
    labels = [],
    has120Vac = false,
    has230Vac = false,
    has277Vac = false,
    has347Vac = false,
    has480Vac = false,
    has120_230Vac = false,
    has120_277Vac = false,
    has347_480Vac = false;

  inputVoltages.map((inputVoltage) => {
    labels[inputVoltage.id] = inputVoltage.name;

    switch (inputVoltage.id) {
      case InputVoltages.i120Vac:
        has120Vac = true;
        break;
      case InputVoltages.i230Vac:
        has230Vac = true;
        break;
      case InputVoltages.i277Vac:
        has277Vac = true;
        break;
      case InputVoltages.i347Vac:
        has347Vac = true;
        break;
      case InputVoltages.i480Vac:
        has480Vac = true;
        break;
      case InputVoltages.i120_230Vac:
        has120_230Vac = true;
        break;
      case InputVoltages.i120_277Vac:
        has120_277Vac = true;
        break;
      case InputVoltages.i347_480Vac:
        has347_480Vac = true;
        break;
    }
  });

  if (has347_480Vac && (has347Vac || has480Vac)) {
    return labels[InputVoltages.i347_480Vac];
  }

  if (has120_277Vac && (has120Vac || has277Vac)) {
    return labels[InputVoltages.i120_277Vac];
  }

  if (has120_230Vac && (has120Vac || has230Vac)) {
    return labels[InputVoltages.i120_230Vac];
  }

  for (let l of labels) {
    if (l !== undefined) {
      label.push(l);
    }
  }

  return label.join(",");
}

export default Vue.extend({
  name: "CellRenderer",
  template: `
       <div>
          <template v-if="isLoggedIn">
            <pinIcon
              v-if="!multiLength"
              v-show="isSelected || isPinned"
              class="pin-icon"
              :class="{'semi-transparent': !isPinned}"
              @click="pinRow">
            </pinIcon>
          </template>
          <span v-html="getValue">
          </span>
       </div>
    `,
  data: function () {
    return {
      uid: null,
      type: null,
      field: null,
      urlFields: ["ulUrl", "csaUrl", "inertekUrl", "file3dUrl", "datasheetUrl"],
      arrayFields: [
        "dimmingTypes",
        "mountingTypes",
        "features",
        "certifications",
      ],
      roundOneFields: ["opMax", "ovMin", "ovMax", "housing.height"],
      roundTwoFields: ["housing.height", "housing.width", "housing.length"],
      timesThousandFields: [
        "ocMin",
        "ocMax",
        "calculationResult.operatingCurrent",
      ],
      roundFields: [
        "calculationResult.outputFluxOfArray",
        "calculationResult.luminousEfficacyOfModule",
        "calculationResult.thermalPower",
      ],
      oneDecimalFields: [
        "calculationResult.power",
        "calculationResult.outputVoltageOfArray",
      ],
    };
  },
  components: {
    pinIcon,
  },
  computed: {
    ...mapState("brain/modules", {
      module: "module",
    }),
    ...mapState("brain/drivers", {
      driver: "driver",
    }),
    ...mapState("brain/global", {
      modulePinResponse: "modulePinResponse",
      driverPinResponse: "driverPinResponse",
    }),
    ...mapState("brain", ["region"]),
    ...mapGetters("brain/auth", ["isLoggedIn"]),
    ...mapGetters("brain/solutions", ["multiLength"]),
    isSelected() {
      if (this.type === "module") {
        return this.module ? this.module.uid === this.uid : false;
      } else {
        return this.driver ? this.driver.uid === this.uid : false;
      }
    },
    isPinned() {
      //Method that checks the pinned list in the store, to see if this module or driver is in their pinnedList.
      if (this.type === "module") {
        return this.$store.getters["brain/modules/pinnedModules"].some(
          (item) => {
            return item.uid === this.uid;
          }
        );
      } else {
        return this.$store.getters["brain/drivers/pinnedDrivers"].some(
          (item) => {
            return item.uid === this.uid;
          }
        );
      }
    },
    getValue() {
      let output = this.params.value;

      if (
        this.field === "name" ||
        (this.region === REGION_NA && this.field === "marketingName")
      ) {
        if (this.params.data.phaseIn && isPhaseIn(this.params.data.phaseIn)) {
          output += '<span class="phase-in" > [new] </span>';
        }

        if (
          this.params.data.phaseOut &&
          isPhaseOut(this.params.data.phaseOut)
        ) {
          output += '<span class="phase-out"> [obsolete] </span>';
        }

        if (this.params.data.phaseIn && isPreCr(this.params.data.phaseIn)) {
          output += '<span class="pre-cr"> [coming soon]</span> ';
        }

        if (this.params.data.isArchived) {
          output += '<span class="is-archived"> [archived]</span>';
        }
      }

      if (this.urlFields.includes(this.field)) {
        return this.params.value !== ""
          ? `<a href="${this.params.value}" target="_blank">View</a>`
          : "-";
      }

      if (this.arrayFields.includes(this.field)) {
        return this.params.value.map((item) => item.name).join(", ");
      }

      if (this.roundOneFields.includes(this.field)) {
        return roundN(this.params.value, 1);
      }

      if (this.roundTwoFields.includes(this.field)) {
        return roundN(this.params.value, 2);
      }

      if (this.timesThousandFields.includes(this.field)) {
        return Math.round(this.params.value * 1000);
      }

      if (this.field === "inputVoltages") {
        return getInputVoltage(this.params.value);
      }

      if (this.field === "classification") {
        if (this.params.value != null) {
          return this.params.value.name === "Class 2";
        }
        return "";
      }

      if (this.field === "driverConfigurability") {
        return this.params.value.length > 0
          ? this.params.value.join(", ")
          : "-";
      }

      if (this.oneDecimalFields.includes(this.field)) {
        return Math.round(this.params.value * 10) / 10;
      }

      return output;
    },
  },
  mounted() {
    this.type = this.params.type;
    this.uid = this.params.data.uid;
    this.field = this.params.field;
  },
  methods: {
    pinRow() {
      if (this.isPinned) {
        this.unPinRow();
      } else {
        this.$store
          .dispatch("brain/global/savePinnedProduct", {
            uid: this.params.data.uid,
            type: this.type,
          })
          .then(() => {
            this.$store.dispatch("brain/global/getPinnedProducts");
          });
      }
    },
    unPinRow() {
      this.$store.dispatch("brain/global/deletePinnedProduct", {
        type: this.type,
        uid: this.params.data.uid,
      });
    },
  },
});
